import {Subtitle, SubtitleStyle} from '@/models/Subtitle';
import {LineBounds, lineBoundsBox} from '@/models/SubtitleStyle';
import {SubtitleBounds, SubtitleWithBounds} from '@/models/SubtitleData';
import {
    calculateCharDimensions,
    measureContentDimensions,
    measureContentHeight,
    measureTextHeight,
    measureTextWidth,
} from '@/utils/font/textMeasurement';
import {
    getAssToCssScaleFactor,
    getDefaultFont,
    getLineHeightRatio,
    getSupportedFonts
} from '@/services/googleFontsService';
import {getUniqueCharacters} from '@/utils/font/characterUtils';

interface FontMetrics {
    scaleFactor: number;      // ASS-to-CSS conversion factor
    lineHeightRatio: number;  // Line height as ratio of font size
}

const MASKING_CONFIG = {
    TARGET_AREA_FILL: 0.85,
    MAX_SCREEN_USAGE: 0.90,
    DENSITY_THRESHOLD: 1.5,
    DENSITY_SCALING_STRENGTH: 0.4,
    MIN_FONT_SIZE_RATIO: 0.6,
    PADDING_X_FACTOR: 0.4,
    PADDING_Y_FACTOR: 0,
    MAX_PADDING_X_PERCENT: 0.02,
    MAX_PADDING_Y_PERCENT: 0.02,
    MIN_FONT_SIZE: 8,
    MAX_FONT_SIZE: 200,
    MIN_TEMPORAL_OVERLAP_MS: 10,
    MIN_REGION_MATCH_IOU: 0.9,
    MIN_LINE_HEIGHT_RATIO: 0.6,
    MIN_ROW_FILL: 0.6,
    MAX_REBREAK_LINES: 3,
    TEXT_COLOR: 'rgba(255, 255, 255, 1)',
    BOX_COLOR: 'rgba(0, 0, 0, 1)',
};

interface CombinedEntry {
    subtitle: Subtitle;
    originalBounds: SubtitleBounds;
    originalLineCount: number;
    originalLineBounds?: SubtitleBounds[];
}

interface MaskingResult {
    updatedSubtitles: Subtitle[];
    styledCount: number;
    skippedCount: number;
}

function calculateUnionBounds(bounds: SubtitleBounds[]): SubtitleBounds {
    if (bounds.length === 0) {
        return {x: 0, y: 0, width: 0, height: 0};
    }

    const minX = Math.min(...bounds.map(b => b.x));
    const minY = Math.min(...bounds.map(b => b.y));
    const maxX = Math.max(...bounds.map(b => b.x + b.width));
    const maxY = Math.max(...bounds.map(b => b.y + b.height));

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function temporalIoU(a: Subtitle, b: SubtitleWithBounds): number {
    const intersection = Math.min(a.endTimeMs, b.endTimeMs) - Math.max(a.startTimeMs, b.startTimeMs);
    const union = Math.max(a.endTimeMs, b.endTimeMs) - Math.min(a.startTimeMs, b.startTimeMs);
    return union > 0 ? Math.max(intersection, 0) / union : 0;
}

/**
 * A near-identical time window (translations preserve timestamps 1:1) identifies
 * the original this cue was derived from, and its bounds are the region to mask —
 * not every region active in the window. The match must also be unambiguous:
 * concurrent cues in different regions can share a window and tie near IoU 1.0.
 * With no confident match (merged or retimed cues) or several (tied windows),
 * the union of all overlapping regions is the safe cover.
 */
function filterToMatchedRegion(subtitle: Subtitle, overlapping: SubtitleWithBounds[]): SubtitleWithBounds[] {
    const matches = overlapping.filter(o => temporalIoU(subtitle, o) >= MASKING_CONFIG.MIN_REGION_MATCH_IOU);
    return matches.length === 1 ? matches : overlapping;
}

function matchTranslatedToOriginal(
    translatedSubtitles: Subtitle[],
    originalBounds: SubtitleWithBounds[]
): CombinedEntry[] {
    const combined: CombinedEntry[] = [];

    const sortedOriginal = [...originalBounds].sort((a, b) => a.startTimeMs - b.startTimeMs);

    for (const subtitle of translatedSubtitles) {
        const overlapping: SubtitleWithBounds[] = [];

        for (const original of sortedOriginal) {
            if (original.startTimeMs > subtitle.endTimeMs) {
                break;
            }

            if (original.endTimeMs > subtitle.startTimeMs) {
                const overlapStart = Math.max(original.startTimeMs, subtitle.startTimeMs);
                const overlapEnd = Math.min(original.endTimeMs, subtitle.endTimeMs);
                const overlapDuration = overlapEnd - overlapStart;

                if (overlapDuration > MASKING_CONFIG.MIN_TEMPORAL_OVERLAP_MS) {
                    overlapping.push(original);
                }
            }
        }

        if (overlapping.length > 0) {
            const matched = filterToMatchedRegion(subtitle, overlapping);
            const unionBounds = calculateUnionBounds(matched.flatMap(o => o.bounds));
            const lineCounts = matched.map(o => Math.max(1, (o.text || '').split('\n').length));

            // Older extractions boxed a whole cue at once, which says nothing about its lines
            const hasLineBounds = matched.length === 1 && matched[0].bounds.length === lineCounts[0];

            combined.push({
                subtitle,
                originalBounds: unionBounds,
                originalLineCount: Math.max(...lineCounts),
                originalLineBounds: hasLineBounds ? matched[0].bounds : undefined,
            });
        }
    }

    return combined;
}

/**
 * OCR also boxes stray text beside the subtitle (logos, signs, word fragments), and
 * fitting rows to those would look broken, so per-line rows are only used when the
 * boxes read as subtitle lines: stacked top to bottom at a similar text height.
 */
function asStackedLines(lineBounds: SubtitleBounds[] | undefined): SubtitleBounds[] | null {
    if (!lineBounds?.length) {
        return null;
    }

    const lines = [...lineBounds].sort((a, b) => a.y - b.y);
    const maxHeight = Math.max(...lines.map(line => line.height));
    const isStacked = lines.every((line, i) =>
        line.height >= maxHeight * MASKING_CONFIG.MIN_LINE_HEIGHT_RATIO &&
        (i === 0 || line.y >= lines[i - 1].y + lines[i - 1].height / 2)
    );

    return isStacked ? lines : null;
}

/**
 * One row per translated line, stacked down from the top of the original region.
 * Each row is wide enough to cover its original line around the shared center, and
 * rows meet halfway between original lines so the stack covers the whole region.
 * Surplus original lines fold into the last row; surplus translated lines get rows
 * sized by their own text.
 */
function calculateLineBounds(
    originalLines: SubtitleBounds[],
    lineCount: number,
    centerX: number,
    textRowHeight: number
): LineBounds[] {
    const rowTops = originalLines.map((line, i) =>
        i === 0 ? line.y : (originalLines[i - 1].y + originalLines[i - 1].height + line.y) / 2
    );
    const lastLine = originalLines[originalLines.length - 1];
    const regionBottom = lastLine.y + lastLine.height;

    return Array.from({length: lineCount}, (_, i) => {
        if (i >= originalLines.length) {
            return {width: 0, height: Math.ceil(textRowHeight)};
        }

        const covered = i === lineCount - 1 ? originalLines.slice(i) : [originalLines[i]];
        const rowBottom = rowTops[i + covered.length] ?? regionBottom;
        const halfWidth = Math.max(...covered.map(line => Math.max(centerX - line.x, line.x + line.width - centerX)));

        return {
            width: Math.ceil(2 * halfWidth),
            height: Math.ceil(Math.max(rowBottom - rowTops[i], textRowHeight)),
        };
    });
}

function minRowFill(lines: string[], rows: LineBounds[], lineBoxWidth: (line: string) => number): number {
    return Math.min(...lines.map((line, i) =>
        rows[i].width > 0 ? Math.min(1, lineBoxWidth(line) / rows[i].width) : 1
    ));
}

function splitIntoLines(words: string[], lineCount: number): string[][] {
    if (lineCount === 1) {
        return [[words.join(' ')]];
    }

    const splits: string[][] = [];
    for (let i = 1; i <= words.length - lineCount + 1; i++) {
        const head = words.slice(0, i).join(' ');
        for (const rest of splitIntoLines(words.slice(i), lineCount - 1)) {
            splits.push([head, ...rest]);
        }
    }
    return splits;
}

/**
 * The translation's own breaks rarely match the original's line lengths, and a short
 * line in a row sized for a long original reads as a stray box, so the words are
 * re-broken to fill the rows as evenly as possible. A line opening with a dash marks
 * a speaker change, so those breaks are kept. Splits are enumerated exhaustively, which
 * is only cheap for a few lines.
 */
function breakLinesToRows(
    lines: string[],
    rows: LineBounds[],
    lineBoxWidth: (line: string) => number,
    maxLineBoxWidth: number
): string[] {
    if (lines.length > MASKING_CONFIG.MAX_REBREAK_LINES || lines.slice(1).some(line => /^[-–—]/.test(line.trim()))) {
        return lines;
    }

    const words = lines.join(' ').split(/\s+/).filter(word => word.length > 0);
    let bestLines = lines;
    let bestFill = minRowFill(lines, rows, lineBoxWidth);

    for (const candidate of splitIntoLines(words, lines.length)) {
        if (candidate.some(line => lineBoxWidth(line) > maxLineBoxWidth)) {
            continue;
        }

        const fill = minRowFill(candidate, rows, lineBoxWidth);
        if (fill > bestFill) {
            bestLines = candidate;
            bestFill = fill;
        }
    }

    return bestLines;
}

function calculateTextDensityRatio(translatedSubtitles: Subtitle[], originalBounds: SubtitleWithBounds[]): number {
    let totalTranslatedLength = 0;
    let totalOriginalLength = 0;

    for (const subtitle of translatedSubtitles) {
        totalTranslatedLength += subtitle.text.replace(/\s/g, '').length;
    }

    for (const original of originalBounds) {
        totalOriginalLength += (original.text || '').replace(/\s/g, '').length;
    }

    if (totalOriginalLength === 0) {
        return 1.0;
    }

    return totalTranslatedLength / totalOriginalLength;
}

function calculateAreaFillRatio(
    text: string,
    fontSize: number,
    boundsWidth: number,
    boundsHeight: number,
    fontFamily: string,
    metrics: FontMetrics
): number {
    const effectiveFontSize = fontSize * metrics.scaleFactor;
    const {width, height} = measureContentDimensions(text, fontFamily, effectiveFontSize, metrics.lineHeightRatio);

    if (boundsWidth <= 0 || boundsHeight <= 0) {
        return 0.0;
    }

    const textArea = width * height;
    const boundsArea = boundsWidth * boundsHeight;

    return textArea / boundsArea;
}

function textExceedsBounds(
    text: string,
    fontSize: number,
    boundsWidth: number,
    boundsHeight: number,
    fontFamily: string,
    metrics: FontMetrics
): boolean {
    const effectiveFontSize = fontSize * metrics.scaleFactor;
    const {width, height} = measureContentDimensions(text, fontFamily, effectiveFontSize, metrics.lineHeightRatio);
    return width > boundsWidth || height > boundsHeight;
}

function calculateOptimalFontSize(
    text: string,
    boundsWidth: number,
    boundsHeight: number,
    fontFamily: string,
    metrics: FontMetrics,
    targetAreaFill: number = MASKING_CONFIG.TARGET_AREA_FILL
): number {
    let low = MASKING_CONFIG.MIN_FONT_SIZE;
    let high = MASKING_CONFIG.MAX_FONT_SIZE;
    let bestSize = low;
    let bestError = Infinity;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);

        if (textExceedsBounds(text, mid, boundsWidth, boundsHeight, fontFamily, metrics)) {
            high = mid - 1;
            continue;
        }

        const areaFill = calculateAreaFillRatio(text, mid, boundsWidth, boundsHeight, fontFamily, metrics);
        const error = Math.abs(areaFill - targetAreaFill);

        if (error < bestError) {
            bestError = error;
            bestSize = mid;
        }

        if (areaFill < targetAreaFill) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return bestSize;
}

function calculateDensityScalingFactor(densityRatio: number): number {
    if (densityRatio <= MASKING_CONFIG.DENSITY_THRESHOLD) {
        return 1.0;
    }

    const excessDensity = densityRatio / MASKING_CONFIG.DENSITY_THRESHOLD;
    const scalingFactor = 1.0 / (1.0 + MASKING_CONFIG.DENSITY_SCALING_STRENGTH * Math.log(excessDensity));

    return Math.max(scalingFactor, MASKING_CONFIG.MIN_FONT_SIZE_RATIO);
}

function applyDensityAdjustedFontSize(optimalFontSize: number, densityRatio: number): number {
    const scalingFactor = calculateDensityScalingFactor(densityRatio);
    return Math.round(optimalFontSize * scalingFactor);
}

async function selectFontForText(text: string, preferredFont: string): Promise<string> {
    const chars = getUniqueCharacters([text]);
    const supportedFonts = await getSupportedFonts(chars, 0.85);

    if(!supportedFonts.length) {
        return preferredFont;
    }

    const topCoverage = supportedFonts[0].coverage;
    const candidates = supportedFonts.filter(f => f.coverage === topCoverage).map(f => f.font);

    if (candidates.includes(preferredFont)) {
        return preferredFont;
    }

    return getDefaultFont(candidates);
}

function wrapTextToLines(
    text: string,
    targetLines: number,
    maxWidthPerLine: number,
    fontFamily: string,
    fontSize: number,
    metrics: FontMetrics
): string[] | null {
    if (targetLines <= 0) {
        return null;
    }

    // Use effective fontSize for accurate measurement
    const effectiveFontSize = fontSize * metrics.scaleFactor;

    if (targetLines === 1) {
        const width = measureTextWidth(text, fontFamily, effectiveFontSize);
        return width <= maxWidthPerLine ? [text] : null;
    }

    const words = text.split(' ');
    if (words.length === 0) {
        return [''];
    }

    const lines: string[] = [];
    let currentLine: string[] = [];

    for (const word of words) {
        const testLine = [...currentLine, word];
        const testLineText = testLine.join(' ');
        const lineWidth = measureTextWidth(testLineText, fontFamily, effectiveFontSize);

        if (lineWidth <= maxWidthPerLine) {
            currentLine = testLine;
        } else {
            if (currentLine.length > 0) {
                lines.push(currentLine.join(' '));
            }
            currentLine = [word];
            if (lines.length >= targetLines) {
                return null;
            }
        }
    }

    if (currentLine.length > 0) {
        lines.push(currentLine.join(' '));
    }

    return lines.length <= targetLines ? lines : null;
}

function greedyWrap(
    words: string[],
    maxWidth: number,
    fontFamily: string,
    effectiveFontSize: number
): string[] {
    if (words.length === 0) return [''];

    const lines: string[] = [];
    let currentLine: string[] = [];

    for (const word of words) {
        const testLine = [...currentLine, word];
        const testLineText = testLine.join(' ');
        const lineWidth = measureTextWidth(testLineText, fontFamily, effectiveFontSize);

        if (lineWidth <= maxWidth || currentLine.length === 0) {
            currentLine = testLine;
        } else {
            lines.push(currentLine.join(' '));
            currentLine = [word];
        }
    }

    if (currentLine.length > 0) {
        lines.push(currentLine.join(' '));
    }

    return lines;
}

function balancedWrapToLines(
    text: string,
    targetLines: number,
    maxWidth: number,
    minWidth: number,
    fontFamily: string,
    fontSize: number,
    metrics: FontMetrics
): string[] | null {
    if (targetLines <= 0) return null;

    const effectiveFontSize = fontSize * metrics.scaleFactor;
    const words = text.split(' ').filter(w => w.length > 0);
    if (words.length === 0) return [''];

    const maxResult = greedyWrap(words, maxWidth, fontFamily, effectiveFontSize);
    if (maxResult.length > targetLines) return null;
    if (targetLines === 1) return maxResult.length === 1 ? maxResult : null;

    const minPossible = Math.max(...words.map(w => measureTextWidth(w, fontFamily, effectiveFontSize)));
    let low = Math.ceil(minPossible);
    let high = Math.floor(maxWidth);
    let bestWidth = maxWidth;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const result = greedyWrap(words, mid, fontFamily, effectiveFontSize);
        if (result.length <= targetLines) {
            bestWidth = mid;
            high = mid - 1;
        } else {
            low = mid + 1;
        }
    }

    const finalWidth = Math.max(bestWidth, minWidth);
    return greedyWrap(words, finalWidth, fontFamily, effectiveFontSize);
}

function forceSplitToLines(
    text: string,
    targetLines: number,
    fontFamily: string,
    effectiveFontSize: number
): string[] {
    if (targetLines <= 0 || targetLines === 1) {
        return [text];
    }

    const words = text.split(' ');
    if (words.length === 0) {
        return Array(targetLines).fill('');
    }

    if (words.length <= targetLines) {
        const lines: string[] = [];
        for (let i = 0; i < targetLines; i++) {
            lines.push(i < words.length ? words[i] : '');
        }
        return lines;
    }

    const minPossible = Math.max(...words.map(w => measureTextWidth(w, fontFamily, effectiveFontSize)));
    const totalWidth = measureTextWidth(text, fontFamily, effectiveFontSize);

    let low = Math.ceil(minPossible);
    let high = Math.ceil(totalWidth);
    let bestLines = greedyWrap(words, totalWidth, fontFamily, effectiveFontSize);

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const result = greedyWrap(words, mid, fontFamily, effectiveFontSize);
        if (result.length <= targetLines) {
            bestLines = result;
            high = mid - 1;
        } else {
            low = mid + 1;
        }
    }

    return bestLines;
}

function calculateProportionalPadding(
    fontFamily: string,
    fontSize: number,
    videoWidth: number,
    videoHeight: number,
    metrics: FontMetrics
): { x: number; y: number } {
    // Use effective fontSize for character dimension calculation
    const effectiveFontSize = fontSize * metrics.scaleFactor;
    const {
        width: charWidth,
        height: charHeight
    } = calculateCharDimensions(fontFamily, effectiveFontSize, metrics.lineHeightRatio);

    const basePaddingX = MASKING_CONFIG.PADDING_X_FACTOR * charWidth;
    const basePaddingY = MASKING_CONFIG.PADDING_Y_FACTOR * charHeight;

    const maxPaddingX = videoWidth * MASKING_CONFIG.MAX_PADDING_X_PERCENT;
    const maxPaddingY = videoHeight * MASKING_CONFIG.MAX_PADDING_Y_PERCENT;

    const paddingX = Math.min(basePaddingX, maxPaddingX);
    const paddingY = Math.min(basePaddingY, maxPaddingY);

    return {x: Math.round(paddingX), y: Math.round(paddingY)};
}

function fitTextProgressively(
    text: string,
    originalBounds: SubtitleBounds,
    fontSize: number,
    fontFamily: string,
    originalLineCount: number,
    padding: { x: number; y: number },
    metrics: FontMetrics,
    videoWidth: number
): string {
    const availableWidth = originalBounds.width - 2 * padding.x;
    const availableHeight = originalBounds.height - 2 * padding.y;

    // Use effective fontSize for all measurements
    const effectiveFontSize = fontSize * metrics.scaleFactor;

    if (availableWidth <= 0 || availableHeight <= 0) {
        console.warn('Invalid available dimensions');
        return text;
    }

    // First, check if the text as-is (with existing line breaks) fits
    const existingLines = text.split('\n');
    const maxExistingLineWidth = Math.max(
        ...existingLines.map(line => measureTextWidth(line, fontFamily, effectiveFontSize))
    );
    const existingHeight = measureContentHeight(text, fontFamily, effectiveFontSize, metrics.lineHeightRatio);

    if (maxExistingLineWidth <= availableWidth && existingHeight <= availableHeight) {
        return text;
    }

    // If existing line breaks don't work, try rewrapping
    // Remove existing line breaks first
    const textWithoutBreaks = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();

    for (let numLines = 1; numLines <= originalLineCount; numLines++) {
        const wrappedLines = wrapTextToLines(textWithoutBreaks, numLines, availableWidth, fontFamily, fontSize, metrics);

        if (wrappedLines) {
            const wrappedText = wrappedLines.join('\n');
            const totalHeight = measureContentHeight(wrappedText, fontFamily, effectiveFontSize, metrics.lineHeightRatio);
            if (totalHeight <= availableHeight) {
                return wrappedText;
            }
        }
    }

    const maxWidth = videoWidth * MASKING_CONFIG.MAX_SCREEN_USAGE - 2 * padding.x;

    const balancedLines = balancedWrapToLines(
        textWithoutBreaks, originalLineCount, maxWidth, availableWidth,
        fontFamily, fontSize, metrics
    );
    if (balancedLines) {
        return balancedLines.join('\n');
    }

    for (let numLines = originalLineCount + 1; numLines <= originalLineCount + 5; numLines++) {
        const wrappedLines = balancedWrapToLines(
            textWithoutBreaks, numLines, maxWidth, availableWidth,
            fontFamily, fontSize, metrics
        );
        if (wrappedLines) {
            return wrappedLines.join('\n');
        }
    }

    return forceSplitToLines(textWithoutBreaks, originalLineCount, fontFamily, effectiveFontSize).join('\n');
}

export async function generateMaskingStyles(
    currentSubtitles: Subtitle[],
    originalBounds: SubtitleWithBounds[],
    videoWidth: number,
    videoHeight: number,
    selectedFont: string,
    backgroundMode: SubtitleStyle['backgroundMode'],
): Promise<MaskingResult> {
    const combinedEntries = matchTranslatedToOriginal(currentSubtitles, originalBounds);

    const densityRatio = calculateTextDensityRatio(currentSubtitles, originalBounds);
    const fontMetricsCache = new Map<string, FontMetrics>();
    const fontSizes = new Map<string, number>();

    const getFontMetrics = async (fontFamily: string): Promise<FontMetrics> => {
        if (!fontMetricsCache.has(fontFamily)) {
            const metrics: FontMetrics = {
                scaleFactor: await getAssToCssScaleFactor(fontFamily),
                lineHeightRatio: await getLineHeightRatio(fontFamily)
            };
            fontMetricsCache.set(fontFamily, metrics);
        }
        return fontMetricsCache.get(fontFamily)!;
    };

    const calculateFontSizeForFont = async (fontFamily: string): Promise<number> => {
        if (fontSizes.has(fontFamily)) {
            return fontSizes.get(fontFamily)!;
        }

        const metrics = await getFontMetrics(fontFamily);
        const originalSampleSize = Math.min(15, originalBounds.length);
        const originalSamples = originalBounds
            .map(original => ({text: original.text, bounds: calculateUnionBounds(original.bounds)}))
            .filter(b => b.bounds.width > 10 && b.bounds.height > 10 && b.text && b.text.trim().length > 0)
            .slice(0, originalSampleSize);

        const adjustedTargetFill = MASKING_CONFIG.TARGET_AREA_FILL;
        const optimalSizes: number[] = [];

        for (const sample of originalSamples) {
            const optimalSize = calculateOptimalFontSize(
                sample.text,
                sample.bounds.width,
                sample.bounds.height,
                fontFamily,
                metrics,
                adjustedTargetFill
            );
            optimalSizes.push(optimalSize);
        }

        optimalSizes.sort((a, b) => a - b);
        const consensusSize = optimalSizes[Math.floor(optimalSizes.length / 2)];
        const adjustedSize = applyDensityAdjustedFontSize(consensusSize, densityRatio);

        fontSizes.set(fontFamily, adjustedSize);
        return adjustedSize;
    };

    const updatedSubtitles: Subtitle[] = [];
    let styledCount = 0;
    let skippedCount = 0;

    const combinedMap = new Map<string, CombinedEntry>();
    for (const entry of combinedEntries) {
        combinedMap.set(entry.subtitle.id, entry);
    }

    for (let i = 0; i < currentSubtitles.length; i++) {
        const subtitle = currentSubtitles[i];
        const combined = combinedMap.get(subtitle.id);

        if (!combined) {
            updatedSubtitles.push(subtitle);
            skippedCount++;
            continue;
        }

        const fontFamily = await selectFontForText(subtitle.text, selectedFont);
        const fontSize = await calculateFontSizeForFont(fontFamily);
        const metrics = await getFontMetrics(fontFamily);

        const padding = calculateProportionalPadding(fontFamily, fontSize, videoWidth, videoHeight, metrics);

        const fittedText = fitTextProgressively(
            subtitle.text,
            combined.originalBounds,
            fontSize,
            fontFamily,
            combined.originalLineCount,
            padding,
            metrics,
            videoWidth
        );

        const effectiveFontSize = fontSize * metrics.scaleFactor;
        const lineBoxWidth = (line: string) => measureTextWidth(line, fontFamily, effectiveFontSize) + 2 * padding.x;
        const originalCenterX = combined.originalBounds.x + combined.originalBounds.width / 2;
        const originalLines = backgroundMode === 'per-line' ? asStackedLines(combined.originalLineBounds) : null;
        const fittedLines = fittedText.split('\n');

        let finalLines = fittedLines;
        let lineBounds = originalLines && calculateLineBounds(
            originalLines,
            fittedLines.length,
            originalCenterX,
            effectiveFontSize * metrics.lineHeightRatio + 2 * padding.y
        );

        if (lineBounds) {
            const maxLineBoxWidth = videoWidth * MASKING_CONFIG.MAX_SCREEN_USAGE;
            const rowLines = breakLinesToRows(fittedLines, lineBounds, lineBoxWidth, maxLineBoxWidth);

            if (minRowFill(rowLines, lineBounds, lineBoxWidth) >= MASKING_CONFIG.MIN_ROW_FILL) {
                finalLines = rowLines;
            } else {
                lineBounds = null;
            }
        }

        const finalText = finalLines.join('\n');
        const finalTextWidth = Math.max(...finalLines.map(line => measureTextWidth(line, fontFamily, effectiveFontSize)));
        const finalTextHeight = measureTextHeight(finalText, fontFamily, effectiveFontSize, metrics.lineHeightRatio);
        const finalBoxWidth = finalTextWidth + 2 * padding.x;
        const finalBoxHeight = finalTextHeight + 2 * padding.y;

        let boxWidth: number;
        let boxHeight: number;
        let paddingX: number;
        let paddingY: number;
        let idealCenterY: number;

        if (lineBounds) {
            const rowsBox = lineBoundsBox(lineBounds);
            boxWidth = Math.max(finalBoxWidth, rowsBox.width);
            boxHeight = rowsBox.height;
            paddingX = padding.x;
            paddingY = padding.y;
            idealCenterY = combined.originalBounds.y + boxHeight / 2;
        } else {
            // The single box must be at least as large as the original bounds to cover them,
            // so grow its padding around the text
            boxWidth = Math.max(finalBoxWidth, combined.originalBounds.width);
            boxHeight = Math.max(finalBoxHeight, combined.originalBounds.height);
            paddingX = Math.max(0, Math.ceil((boxWidth - finalTextWidth) / 2));
            paddingY = Math.max(0, Math.ceil((boxHeight - finalTextHeight) / 2));
            idealCenterY = combined.originalBounds.y + combined.originalBounds.height / 2;
        }

        // Calculate center position, clamped to MAX_SCREEN_USAGE margins
        const margin = (1.0 - MASKING_CONFIG.MAX_SCREEN_USAGE) / 2.0;
        const minCenterX = videoWidth * margin + boxWidth / 2;
        const maxCenterX = videoWidth * (1.0 - margin) - boxWidth / 2;
        const minCenterY = videoHeight * margin + boxHeight / 2;
        const maxCenterY = videoHeight * (1.0 - margin) - boxHeight / 2;

        const centerX = (Math.max(minCenterX, Math.min(originalCenterX, maxCenterX)) / videoWidth) * 100;
        const centerY = (Math.max(minCenterY, Math.min(idealCenterY, maxCenterY)) / videoHeight) * 100;

        const maskingStyle: SubtitleStyle = {
            fontSize,
            fontFamily,
            paddingX,
            paddingY,
            centerX,
            centerY,
            color: MASKING_CONFIG.TEXT_COLOR,
            backgroundColor: MASKING_CONFIG.BOX_COLOR,
            backgroundMode: lineBounds ? 'per-line' : 'unified',
            fontWeight: 'normal',
            fontStyle: 'normal',
            ...(lineBounds && {lineBounds}),
        };

        const updatedSubtitle = new Subtitle({
            ...subtitle,
            text: finalText,
            style: maskingStyle,
        });

        updatedSubtitles.push(updatedSubtitle);
        styledCount++;
    }

    return {
        updatedSubtitles,
        styledCount,
        skippedCount,
    };
}
